import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useMemo, useCallback, useState } from 'react';
import { styled } from 'styled-components';
import { cellSkeletonMap, cellMap } from './cellMap.js';
import { cellHeaderMap, selectableColumns } from './cellMapHeader.js';
import { defaultColumnConfigMap } from './defaultColumnConfigMap.js';
import { ResizeHandles } from './ResizeHandles.js';
import { ResourcePreviewContextMenu } from '../../ResourcePreview/ResourcePreviewContextMenu/ResourcePreviewContextMenu.js';
import { ContextMenu } from '@fabric-app/ui/components/ContextMenu/ContextMenu';
import { IntersectionObserverAnchor } from '@fabric-app/ui/components/IntersectionObserver/IntersectionObserver';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { VirtualTableRow, VirtualDataTable } from '@fabric-app/ui/components/VirtualDataTable/VirtualDataTable';
import { useResourceListPreferencesContext } from '../contexts/resourceListContextPreferences.js';
import { columnTitleMap } from './columnTitleMap.js';
import { ResourceDataContextProvider } from '../../../../resource-detail/components/context/ResourceDataContextProvider.js';
import { IconVoiceNoteCheck } from '@fabric-app/icons/svg';
import { RootType } from '@fabric/woody-client';
import { useSetQueryResourceDetailData } from '../../../queries/useQueryResourceDetail.js';

const VirtualResourceTable = ({ resources, totalResourcesCount, onInfiniteTrigger, scrollYMargin = 0, minWidth, hideHeader = false, colorLabels, onResourceClick, disableItemPreviewContextMenu = false }) => {
  const { setTableConfig: setConfig, tableColumns: config } = useResourceListPreferencesContext();
  const { columns, columnSizes, sortedConfig } = useMemo(() => {
    const uniqueConfig = config.filter((column, index, self) => self.findIndex((c) => c.id === column.id) === index);
    const sortedConfig2 = uniqueConfig.sort((a, b) => a.order - b.order);
    const columns2 = sortedConfig2.map((column) => {
      return {
        id: column.id,
        CellComponent: cellMap[column.id],
        CellSkeletonComponent: cellSkeletonMap[column.id],
        HeaderCellComponent: cellHeaderMap[column.id]
      };
    });
    const columnSizes2 = sortedConfig2.map((c) => c.width);
    return {
      columnSizes: columnSizes2,
      columns: columns2,
      sortedConfig: sortedConfig2
    };
  }, [
    config
  ]);
  const setColumnSize = useCallback((colId, value) => {
    setConfig((cfg) => {
      const newCfg = cfg.map((c) => c.id === colId ? {
        ...c,
        width: value
      } : c);
      return newCfg;
    });
  }, [
    setConfig
  ]);
  const style = useMemo(() => {
    let totalWidth = 0;
    const cellWidthCssVariables = columnSizes.reduce((r, next, index) => {
      totalWidth += next;
      return {
        ...r,
        [`--cell-width-${index}`]: `${next}px`
      };
    }, {});
    return {
      ...cellWidthCssVariables,
      width: totalWidth,
      minWidth,
      "--container-height": `${(totalResourcesCount + (hideHeader ? 0 : 1)) * 41}px`
    };
  }, [
    columnSizes,
    minWidth,
    hideHeader,
    totalResourcesCount
  ]);
  const RowComponent = useMemo(() => {
    const Component = ({ rowIndex, data, children, ...props }) => {
      const setQueryResourceDetailData = useSetQueryResourceDetailData();
      if (data && "kind" in data) {
        const resource = data;
        const isExternalResource = !!resource._meta?.externalData?.url;
        return /* @__PURE__ */ jsx(ResourceDataContextProvider, {
          resource,
          children: /* @__PURE__ */ jsx(ResourcePreviewContextMenu, {
            contextDisabled: disableItemPreviewContextMenu || isExternalResource,
            onItemClickAction: onResourceClick ? () => onResourceClick(resource) : void 0,
            colorLabels,
            children: /* @__PURE__ */ jsx(RowBase, {
              ...props,
              onMouseOver: () => {
                setQueryResourceDetailData(resource);
              },
              onDragStart: (e) => e.preventDefault(),
              "data-selectable-id": resource.isDraft || resource.root?.type === RootType.INTEGRATION ? void 0 : resource?.id,
              onClick: () => {
                onResourceClick(resource);
              },
              style: {
                ...props.style,
                background: `var(--item-selected-${resource.id})`
              },
              children
            })
          })
        });
      }
      return /* @__PURE__ */ jsxs(RowBaseDummy, {
        ...props,
        children: [
          data && "divider" in data ? data.renderContent?.() : children,
          " ",
          !data && rowIndex % 10 === 0 && onInfiniteTrigger ? /* @__PURE__ */ jsx(IntersectionObserverAnchor, {
            onIntersect: onInfiniteTrigger,
            rootMargin: "500px",
            debounceDelay: 200,
            // for some reason on ios this is not triggering with at leat height of 1px
            style: {
              height: 1,
              position: "absolute"
            }
          }, rowIndex) : null
        ]
      });
    };
    return Component;
  }, [
    onInfiniteTrigger,
    disableItemPreviewContextMenu,
    onResourceClick,
    colorLabels
  ]);
  const virtualConfig = {
    itemHeight: 41,
    itemWidth: style.width,
    itemsCount: totalResourcesCount,
    overscan: 30,
    scrollYMargin
  };
  const [highlightedHandleId, setHighlightedHandleId] = useState(null);
  const args = useMemo(() => {
    return {
      isColumnIdVisible: (colId) => {
        return Boolean(config.find((item) => item.id === colId));
      },
      toggleColumn: (colId) => {
        setConfig((cfg) => {
          const foundItem = cfg.find((item) => item.id === colId);
          if (foundItem) {
            return cfg.filter((item) => item.id !== colId);
          } else {
            return [
              ...cfg,
              defaultColumnConfigMap[colId]
            ];
          }
        });
      },
      setHighlightedHandleId,
      highlightedHandleId
    };
  }, [
    config,
    setConfig,
    highlightedHandleId
  ]);
  return /* @__PURE__ */ jsx(VirtualTableWrapper, {
    columnSizes,
    style,
    children: /* @__PURE__ */ jsx(VirtualDataTable, {
      virtualConfig,
      data: resources,
      columns,
      args,
      RowComponent,
      minWidth,
      hideHeader,
      HeaderComponent: Header,
      children: /* @__PURE__ */ jsx(ResizeHandles, {
        highlightedHandleId,
        setColumnSize,
        columnsConfig: sortedConfig
      })
    })
  });
};
const RowBase = styled.div.withConfig({
  displayName: "VirtualResourceTable__RowBase",
  componentId: "sc-801ea2d5-0"
})([
  `display:flex;flex-direction:row;align-items:center;justify-content:flex-start;border-bottom:1px solid `,
  `;height:41px;cursor:pointer;&:hover{background:rgba(`,
  `,0.03) !important;}`
], cssVar["color-border-primary"], cssVar["color-bg-contrast-reverse-rgb"]);
const VirtualTableWrapper = styled.div.withConfig({
  displayName: "VirtualResourceTable__VirtualTableWrapper",
  componentId: "sc-801ea2d5-1"
})([
  `position:relative;`,
  ` div[data-vdt-item-col],div[data-vdt-header-cell]{height:100%;flex-grow:0;}div[data-vdt-item-col],div[data-vdt-header-cell]{&:not(:first-child){border-right:1px solid `,
  `;}}div[data-vdt-header]{border-bottom:1px solid `,
  `;position:sticky;top:0;z-index:20;}`
], (p) => p.columnSizes.map((size, index) => `div[data-vdt-item-col='${index}'], div[data-vdt-header-cell='${index}'] { width: var(--cell-width-${index}); }`).join("\n"), cssVar["color-border-primary"], cssVar["color-border-primary"]);
const HeaderBase = styled(VirtualTableRow).attrs((props) => {
  return {
    "data-vdt-header": true,
    "data-multiselect-clear": true,
    ...props
  };
}).withConfig({
  displayName: "VirtualResourceTable__HeaderBase",
  componentId: "sc-801ea2d5-2"
})([
  `background:`,
  `;border-bottom:1px solid `,
  `;`
], cssVar["color-bg-tertiary"], cssVar["color-border-primary"]);
const Header = ({ args, children, ...rest }) => {
  return /* @__PURE__ */ jsx(ContextMenu, {
    content: /* @__PURE__ */ jsx(Fragment, {
      children: /* @__PURE__ */ jsx(ContextMenu.Group, {
        children: selectableColumns.map((colId) => {
          return /* @__PURE__ */ jsxs(ContextMenu.Item, {
            onSelect: (e) => {
              e.preventDefault();
              args.toggleColumn(colId);
            },
            children: [
              /* @__PURE__ */ jsx(ContextMenuIconContainer, {
                children: args.isColumnIdVisible(colId) && /* @__PURE__ */ jsx(IconVoiceNoteCheck, {
                  style: {
                    width: "1rem"
                  }
                })
              }),
              columnTitleMap[colId]
            ]
          }, colId);
        })
      })
    }),
    children: /* @__PURE__ */ jsx(HeaderBase, {
      ...rest,
      children
    })
  });
};
const RowBaseDummy = styled(RowBase).withConfig({
  displayName: "VirtualResourceTable__RowBaseDummy",
  componentId: "sc-801ea2d5-3"
})([
  `&:hover{background:transparent !important;}cursor:initial;`
]);
const ContextMenuIconContainer = styled.div.withConfig({
  displayName: "VirtualResourceTable__ContextMenuIconContainer",
  componentId: "sc-801ea2d5-4"
})([
  `display:flex;align-items:center;justify-content:center;height:1rem;width:1rem;flex-shrink:0;margin-right:0.625rem;`
]);

export { VirtualResourceTable };
//# sourceMappingURL=VirtualResourceTable.js.map
